import { useContext } from 'react';
import { TmdxResourceCard } from '@features/components/ResourceCards';
import { AuthContext } from '@lp/core/auth';
import { toast } from 'react-toastify';
import { ResourceLogisticsStatus } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { useOcsConsoleByIdQuery } from '@/queries/ocs-consoles';
import { CaseDetailsPermissions } from '@/permissions';
import { getDeviceDisabledKeys, getDeviceMenuItems } from './config';
import { useChangeDeviceStatus } from '@/queries/case';
import { retrieveUnifiedError } from '@/api/utils';
import { resourceLogisticsStatusNames } from '@/constants/resources.constants';

export const OcsDeviceCard = ({
  logisticsStatus,
  deviceId,
  caseId,
}: {
  logisticsStatus: ResourceLogisticsStatus;
  deviceId: Uuid;
  caseId: Uuid;
}) => {
  const { data: ocsConsole } = useOcsConsoleByIdQuery(deviceId, { withDeleted: true });
  const { mutateAsync: mutateDeviceStatus } = useChangeDeviceStatus();
  const { checkPermission } = useContext(AuthContext);
  const hasResourcesStatusUpdatePermission = checkPermission(CaseDetailsPermissions.ResourceConsoleStatusUpdate);

  const changeDeviceStatus = async (
    consoleId: Uuid,
    prevStatus: ResourceLogisticsStatus,
    status: ResourceLogisticsStatus,
  ) => {
    if (prevStatus === status) return;
    try {
      await mutateDeviceStatus({ caseId, consoleId, status });
      toast.success('Status has been changed');
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  if (!ocsConsole) return null;
  return (
    <div key={ocsConsole.id} className="w-80">
      <TmdxResourceCard
        menuDisabled={!hasResourcesStatusUpdatePermission}
        menuProps={{
          disabledKeys: getDeviceDisabledKeys(logisticsStatus),
          selectedKeys: [logisticsStatus],
        }}
        list={getDeviceMenuItems((newStatus: ResourceLogisticsStatus) =>
          changeDeviceStatus(ocsConsole.id, logisticsStatus, newStatus),
        )}
        type="device"
        title={ocsConsole.ocsConsoleName}
        subTitle={ocsConsole.fullSerialNumber}
        phoneNumber=""
        avatarProps={{
          size: 'xl',
          type: 'device',
          organ: ocsConsole.organType,
          src: ocsConsole.logoPath,
        }}
        chipProps={{
          status: logisticsStatus,
        }}
        chipText={resourceLogisticsStatusNames[logisticsStatus]}
      />
    </div>
  );
};
